import { useState , useEffect} from "react";
import useFirestoreData, { loginUtente, registraUtente, googleProvider, auth, CreaDocUt, deleteGoogleAccount } from "./FirestoreDataLoader.tsx";
import { signInWithPopup, User , sendEmailVerification} from "firebase/auth";
import { setPersistence, browserLocalPersistence } from "firebase/auth";
import { CircleCheck } from 'lucide-react';
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useConfirmToast } from "./alertFunction.tsx";
import { db } from './FirestoreDataLoader.tsx'; 
import { doc, getDoc } from 'firebase/firestore';
import { Modal, Box, CircularProgress, Button } from '@mui/material';


// Logica di validazione della password (rimane invariata)
const validatePassword = (password:string) => {
  return {
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[^A-Za-z0-9]/.test(password),
    hasMinLength: password.length >= 6,
  };
};

// Componente principale
export default function App() {

  const { errorAlert, successAlert, warningAlert, infoAlert } = useConfirmToast();
  const { datiCompleti, isDataLoad, userId, tipoAccount } = useFirestoreData();
  const location = useLocation();
  const navigate = useNavigate();

  const [blockLoginEffect, setBlockLoginEffect] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);

  const isPasswordValid = Object.values(validatePassword(password)).every(Boolean);

  //controlla se c’è un token 
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tokenFromUrl = queryParams.get("token");

    if (tokenFromUrl) {
      setIsRegistering(true);
      // Salviamo subito il token 
      sessionStorage.setItem("inviteToken", tokenFromUrl);

      // Recuperiamo i dati da Firestore
      const fetchInviteData = async () => {
        try {
          const inviteRef = doc(db, "inviti", tokenFromUrl);
          const docSnap = await getDoc(inviteRef);

          if (!docSnap.exists()) { errorAlert("Invito già utilizzato o non valido.<br/> Puoi creare un account solo come <strong>azienda.</strong>"); return };

          const data = docSnap.data();

          const inviteData = {
            token: tokenFromUrl,
            emailInv: data.email || null,
            invitanteId: data.invitanteId || null,
            ruolo: data.ruolo || null,
          };

          // Salviamo anche i dati 
          sessionStorage.setItem("inviteData", JSON.stringify(inviteData));
        } catch (err: any) {
          console.error("Errore nel recupero invito:", err.message);
          sessionStorage.removeItem("inviteData");
        }
      };

      fetchInviteData();
    } else {
      // Nessun token 
      sessionStorage.removeItem("inviteToken");
      sessionStorage.removeItem("inviteData");
    }
  }, [location.search]);


  // login 
  useEffect(() => {
    
    if (!isDataLoad || !userId || blockLoginEffect) return; 

    const user = auth.currentUser;
    const handleLogin = async () => {
      if (!user) return;

      const docRef = doc(db, "utenti", String(user.uid));
      const docSnap = await getDoc(docRef);

      setLoading(false);
      if (!docSnap.exists() || !user.emailVerified){
        setIsEmailSent(true);
        warningAlert('Sembra che non hai completato la registrazione!<br/>Controlla la tua casella di posta per un link di verifica e completa la registrazione.');
      } else if (!datiCompleti) {
        warningAlert('Sembra che non hai completato la registrazione!<br/>Completa l\'anagrafica per continuare.');
        setTimeout(() => {
            navigate("/profile");
        }, 500);
      } else if (user.emailVerified) {
        // Tutto verificato e dati completi
        if(tipoAccount == 'cliente'){
          setTimeout(() => {
            navigate("/dashboard");
          }, 500);
        }else{
          setTimeout(() => {
            navigate("/");
          }, 500);
        };
      }; 
    };

    if (user && !isRegistering) {
      handleLogin();
    }
  }, [isDataLoad, datiCompleti, isRegistering, userId, blockLoginEffect]);


  const handleNewRegistration = (registration: boolean = false) => {
    if (registration) {
      setLoading(false);
      successAlert("Registrazione completata", "Benvenuto!");
      setTimeout(() => {
        navigate("/profile");
      }, 1500);
    } 
  };


  const handleAuth = async (action: 'login' | 'register') => {
    setLoading(true);
    try {
      await setPersistence(auth, browserLocalPersistence);
      
      if (action === 'login') {
        //Email/Pass
        const loginSuccessful = await loginUtente(email, password); 
        
        if (loginSuccessful) {
          //await handleLogin();
        } else {
            errorAlert( "Credenziali non valide.", "Accesso fallito");
        }

      } else {
        if (!isPasswordValid) {
          errorAlert( "Per favore, rispetta i requisiti di sicurezza della password.", "Password non valida");
          setLoading(false);
          return;
        }
        
        const userCredential = await registraUtente(email, password); 
        
        if (userCredential) {
            const user: User | null = userCredential.user;
            
            if (user) {
              setLoading(false);
              setIsEmailSent(true);
            } else {
              setLoading(false);
              errorAlert("Utente non autenticato dopo la creazione dell'account.", "Errore di registrazione");
            }
        } else {
          setLoading(false);
          errorAlert("Si è verificato un errore durante la creazione dell'account.", "Registrazione fallita");
        }
      }

    } catch (error: any) {
      setLoading(false);
      errorAlert(error.message);
    } 
  };

  const handleGoogleLogin = async () => {
    setBlockLoginEffect(true);
    setLoading(true);
    try {
      await setPersistence(auth, browserLocalPersistence);
      const result = await signInWithPopup(auth, googleProvider);
      
      const isNewUser = await CreaDocUt(result.user.uid, result.user.email, String(result.user.photoURL)); 
      if (isNewUser) {
        handleNewRegistration(true); 
      } else if (isRegistering && !isNewUser){
        setLoading(false);
        infoAlert('Clicca su Accedi per effetuare il login.')
      } else { 
        setLoading(false);
        setBlockLoginEffect(false);
      }
      
    } catch (error: any) {
      setLoading(false);
      setBlockLoginEffect(false);
      if (error.message === 'Firebase: Error (auth/popup-closed-by-user).'){
        //errorAlert('Non è stato selezionato nessun account.');
      } else { errorAlert(error.message) };
    } 
  };


  // Polling attivo solo sulla schermata di attesa della verifica email
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    const user = auth.currentUser;

  
    if (isEmailSent && user) {
      intervalId = setInterval(async () => {
        // Forza l'aggiornamento dei dati utente da Firebase
        await user.reload(); 
        const reloadedUser = auth.currentUser;

        if (reloadedUser && reloadedUser.emailVerified) {
        
          if (intervalId) clearInterval(intervalId);// Stop Polling

          const isNewUser = await CreaDocUt(user.uid, user.email, String(user.photoURL));
          if (!datiCompleti && isNewUser) {
            successAlert("", "Email verificata con successo.<br/>Benvenuto!");
            setTimeout(() => {
              navigate("/profile");
            }, 1500);
          }
        }
      }, 3000); // Controlla ogni 3 secondi
    }

    //ferma il polling quando il componente smonta o lo stato isEmailSent cambia
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isEmailSent, navigate, successAlert, datiCompleti]);

  const handleSendVerificationEmail = async () => {
  const user = auth.currentUser;
  if (user) {
    setLoading(true); 
    try {
      await sendEmailVerification(user); 
      
      successAlert("Una nuova email di verifica è stata inviata!", "Email Reinviata");
      setIsEmailSent(true);
    } catch (error: any) {
      console.error("Errore nell'invio della verifica email:", error);
      errorAlert("Errore durante il reinvio dell'email. Riprova.", "Errore");
    } finally {
      setLoading(false); 
    }
  } else {
    warningAlert("Utente non trovato. Per favore, prova ad accedere di nuovo.", "Attenzione");
    setIsEmailSent(false); 
  }
};
  // Se l'email è stata inviata, mostra un messaggio 
  if (isEmailSent) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', padding: '1rem' }}>
        <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '1rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', width: '100%', maxWidth: '25rem', border: '1px solid #e5e7eb', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#10b981', marginBottom: '1rem' }}>
            <CircleCheck/> Verifica l'Email per Continuare!
          </h2>
          <p style={{ color: '#4b5563', marginBottom: '1.5rem' }}>
            Abbiamo inviato un'email di verifica all'indirizzo <strong>{email}</strong>.<br/>
            Per favore, controlla la tua casella di posta eletronica <strong>( inclusa la cartella Spam )</strong>  e clicca sul link per attivare il tuo account.
          </p>
          <p style={{color:'#4a525eff'}}>Non hai ricevuto l'email? Riprova :</p>
          <Button variant='contained' color='primary' disabled={loading} onClick={handleSendVerificationEmail} sx={{m:'1rem 0 2rem 0'}}>Invia Nuova Email di Verifica</Button>
          <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            <strong style={{marginRight:'0.3rem'}}>Nota:</strong> se dopo aver effetuato la verifica non vieni reindirizzato sulla prossima sezione,<strong style={{marginLeft:'0.3rem'}}>aggiorna la pagina!</strong>
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', padding: '1rem' }}>
        <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '1rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', width: '100%', maxWidth: '24rem', border: '1px solid #e5e7eb' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: '800', textAlign: 'center', color: '#1f2937', marginBottom: '0.5rem',userSelect:'none' }}>
            Benvenuto!
          </h1>
           <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.875rem', marginBottom: '2rem' ,userSelect:'none'}}>
            AutoFattura.pro
          </p>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', textAlign: 'center', marginBottom: '1.5rem',userSelect:'none' }}>
            {isRegistering ? 'Crea un account' : 'Accedi al tuo account'}
          </h2>

          <form onSubmit={(e) => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', outline: 'none', transition: 'box-shadow 0.2s, border-color 0.2s' }}
              required
            />

            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', outline: 'none', transition: 'box-shadow 0.2s, border-color 0.2s' }}
                required
              />
              <div
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', top: '0', right: '0', bottom: '0', paddingRight: '0.75rem', display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#6b7280' }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </div>
            </div>

            {isRegistering && (
              <>
                <div style={{ padding: '0.5rem', borderRadius: '0.5rem', backgroundColor: '#f3f4f6' }}>
                  <p style={{ fontSize: '0.75rem', color: '#4b5563', textAlign: 'center',userSelect:'none' }}>
                    Dopo la registrazione, ti invieremo un link di verifica all'email. 
                    Dopo la verifica, potrai accedere per inserire i dati aziendali (P.IVA, Chiave API, etc.).
                  </p>
                </div>
                <div style={{ backgroundColor: '#f3f4f6', padding: '1rem', borderRadius: '0.5rem',userSelect:'none' }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    La password deve avere:
                  </p>
                 <ul style={{ fontSize: '0.75rem', color: '#6b7280', listStyleType: 'none', padding: '0', margin: '0', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: validatePassword(password).hasMinLength ? '#16a34a' : '#ef4444' }}>
                      {validatePassword(password).hasMinLength ? '✅' : '❌'}
                      <span>Almeno 6 caratteri</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: validatePassword(password).hasUpperCase ? '#16a34a' : '#ef4444' }}>
                      {validatePassword(password).hasUpperCase ? '✅' : '❌'}
                      <span>Una lettera MAIUSCOLA</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: validatePassword(password).hasLowerCase ? '#16a34a' : '#ef4444' }}>
                      {validatePassword(password).hasLowerCase ? '✅' : '❌'}
                      <span>Una lettera minuscola</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: validatePassword(password).hasNumber ? '#16a34a' : '#ef4444' }}>
                      {validatePassword(password).hasNumber ? '✅' : '❌'}
                      <span>Un numero</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: validatePassword(password).hasSpecialChar ? '#16a34a' : '#ef4444' }}>
                      {validatePassword(password).hasSpecialChar ? '✅' : '❌'}
                      <span>Un carattere speciale (!@#$...)</span>
                    </li>
                  </ul>
                </div>
              </>
            )}

            <button
              type="submit"
              onClick={() => handleAuth(isRegistering ? 'register' : 'login')}
              disabled={loading || (isRegistering && !isPasswordValid)}
              style={{
                width: '100%',
                border:'1px solid #c4c3c3ff',
                paddingTop: '0.75rem',
                paddingBottom: '0.75rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                borderRadius: '0.5rem',
                fontWeight: '600',
                userSelect:'none',
                transitionProperty: 'background-color',
                transitionDuration: '0.2s',
                backgroundColor: loading || (isRegistering && !isPasswordValid) ? '#6fa4f5ff' : '#1167e9ff',
                color: 'white',
                cursor: loading || (isRegistering && !isPasswordValid) ? 'not-allowed' : 'pointer',
              }}
            >
              { isRegistering ? 'Registrati' : 'Accedi'}
            </button>
          </form>

          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', paddingTop: '1.25rem', paddingBottom: '1.25rem' }}>
            <div style={{ flexGrow: 1, borderTop: '1px solid #d1d5db' }}></div>
            <span style={{ flexShrink: 0, margin: '0 1rem', color: '#6b7280',userSelect:'none' }}>oppure</span>
            <div style={{ flexGrow: 1, borderTop: '1px solid #d1d5db' }}></div>
          </div>

           <button
            onClick={handleGoogleLogin}
            disabled={loading}
            style={{
              width: '100%',
              paddingTop: '0.75rem',
              paddingBottom: '0.75rem',
              paddingLeft: '1rem',
              paddingRight: '1rem',
              borderRadius: '0.5rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transitionProperty: 'background-color',
              transitionDuration: '0.2s',
              backgroundColor: loading ? '#f3f4f6' : 'white',
              color: loading ? '#9ca3af' : '#374151',
              border: '1px solid #d1d5db',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            <img src="https://www.svgrepo.com/show/355037/google.svg" alt="Logo Google" style={{ width: '1.25rem', height: '1.25rem' }} />
            <span style= {{userSelect:'none'}}>{isRegistering ? ' Registrati con Google' : ' Accedi con Google'}</span>
          </button>

          <div style={{ textAlign: 'center', fontSize: '0.875rem', marginTop: '1.5rem' }}>
            <button
              onClick={() => setIsRegistering(!isRegistering)}
              style={{ color: '#0044ffff', textDecoration: 'underline', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', userSelect:'none' }}
            >
              {isRegistering ? 'Hai già un account? Accedi' : 'Non hai un account? Registrati'}
            </button>
          </div>
        </div>
      </div>
      <Modal
        open={loading}
        onClose={() => {}} 
        aria-labelledby="loading-modal-title"
        aria-describedby="loading-modal-description"
        slots={{ backdrop: Box }}
        slotProps={{
          backdrop: {
            style: { 
              backgroundColor: '#bebebe54',
            }
          },
        }}
      >
        <Box 
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgColor: 'transparent',
            boxShadow: 0,
            p: 4
          }}
        >
          <CircularProgress color="primary" size={60} />
        </Box>
      </Modal>
    </>
  );
} 

